import { useEffect } from 'react';

import { closeSVGString, zoomSVGString } from '@/lib/const';
import { captureError } from '@/lib/errors';
import { theme } from '@/theme';

export const getPhotoSwipeDataSource = (galleryElement: ParentNode) => {
  const allLinks = Array.from(
    galleryElement.querySelectorAll('a[data-pswp-index]'),
  ) as HTMLElement[];

  const sortedLinks = [...allLinks];
  sortedLinks.sort((a, b) => {
    const indexA = Number.parseInt(a.dataset.pswpIndex ?? '0', 10);
    const indexB = Number.parseInt(b.dataset.pswpIndex ?? '0', 10);
    return indexA - indexB;
  });

  return sortedLinks.map((link) => ({
    src: link.getAttribute('href') || '',
    width: Number.parseInt(link.dataset.pswpWidth ?? '0', 10),
    height: Number.parseInt(link.dataset.pswpHeight ?? '0', 10),
  }));
};

const applyPhotoSwipeCounterStyles = () => {
  const counterElement = document.querySelector('.pswp__counter');
  if (counterElement instanceof HTMLElement) {
    counterElement.style.color = theme.colors.default;
    counterElement.style.textShadow = 'none';
  }
};

const openPhotoSwipeGallery = async (
  galleryElement: ParentNode,
  index: number,
) => {
  const dataSource = getPhotoSwipeDataSource(galleryElement);
  const { default: PhotoSwipe } = await import('photoswipe');
  const pswp = new PhotoSwipe({
    dataSource,
    index,
    bgOpacity: 1,
    showHideAnimationType: 'fade',
    closeSVG: closeSVGString,
    zoomSVG: zoomSVGString,
  });

  pswp.on('afterInit', applyPhotoSwipeCounterStyles);
  pswp.init();
};

export const usePhotoSwipe = (
  isReady: boolean,
  gallery: string | undefined,
) => {
  useEffect(() => {
    if (gallery && isReady) {
      const stringifiedGallery = `#${gallery}`;
      const galleryElement = document.querySelector(stringifiedGallery);

      if (!galleryElement) return;

      let isOpening = false;

      const handleClick = (e: Event) => {
        const linkElement = (e.target as HTMLElement).closest(
          'a[data-pswp-index]',
        ) as HTMLElement | null;

        if (!linkElement) {
          return;
        }

        const index = Number.parseInt(linkElement.dataset.pswpIndex ?? '0', 10);
        if (Number.isNaN(index)) {
          return;
        }

        e.preventDefault();
        if (isOpening) {
          return;
        }

        isOpening = true;
        void openPhotoSwipeGallery(galleryElement, index)
          .catch((error: unknown) => {
            captureError(
              error instanceof Error ? error : new Error(String(error)),
            );
          })
          .finally(() => {
            isOpening = false;
          });
      };

      galleryElement.addEventListener('click', handleClick);

      return () => {
        galleryElement.removeEventListener('click', handleClick);
      };
    }
  }, [gallery, isReady]);

  return { gallery };
};
